/**
 * AccessModal — activation camouflée (inscription → 50 CHF → 60 GC)
 * Liquid glass bleu · flux : welcome → nom → fee → pays → ville → pay → waiting → confirmed
 * Compte créé UNIQUEMENT après confirmation du dépôt.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, CheckCircle, Loader, MapPin, MessageCircle } from 'lucide-react';
import { getApiAuthHeaders } from './utils/apiAuthFetch';
import { safeUserError, GENERIC_USER_ERROR } from './utils/safeUserError';
import AccessSelfieModal from './AccessSelfieModal';
import FloatingChatPanel from './FloatingChatPanel';
import { prepareChatImage } from './utils/prepareChatImage';
import { uploadWithProgress } from './utils/uploadWithProgress';

const GS_LOGO = '/emoji/logoapp1.png';

const GLASS = {
  background: 'linear-gradient(145deg, rgba(0,229,255,0.12) 0%, rgba(0,100,255,0.22) 45%, rgba(112,0,255,0.18) 100%)',
  backdropFilter: 'blur(36px) saturate(220%) brightness(1.12)',
  WebkitBackdropFilter: 'blur(36px) saturate(220%) brightness(1.12)',
  border: '1.5px solid rgba(125,211,252,0.38)',
  boxShadow: '0 12px 48px rgba(0,0,0,0.50), 0 0 40px rgba(0,140,255,0.12), inset 0 1px 0 rgba(255,255,255,0.18)',
};

const OVERLAY_S = {
  position: 'fixed', inset: 0, zIndex: 'var(--gs-z-lock, 4000)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(5,10,30,0.72)',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  padding: '20px 16px',
  backgroundImage: 'url(/emoji/SWISS3.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

const MODAL_S = {
  ...GLASS,
  borderRadius: 26, width: '100%', maxWidth: 400,
  padding: '26px 20px 22px',
  position: 'relative', overflow: 'hidden',
  color: '#fff', fontFamily: "'Inter', system-ui, sans-serif",
  maxHeight: '88vh', overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  pointerEvents: 'auto',
};

const BTN_BLUE = {
  width: '100%', padding: '14px 16px', borderRadius: 14,
  fontWeight: 800, fontSize: 14, color: '#fff', cursor: 'pointer',
  background: 'linear-gradient(145deg, rgba(0,229,255,0.38) 0%, rgba(0,100,255,0.55) 50%, rgba(112,0,255,0.48) 100%)',
  border: '1.5px solid rgba(125,211,252,0.55)',
  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  boxShadow: '0 8px 28px rgba(0,140,255,0.42), 0 0 24px rgba(112,0,255,0.18), inset 0 1px 0 rgba(255,255,255,0.35)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  userSelect: 'none',
};

const BTN_GLASS = {
  ...GLASS, borderRadius: 14, padding: '12px 16px',
  fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.90)',
  cursor: 'pointer', display: 'flex', alignItems: 'center',
  justifyContent: 'center', gap: 8, width: '100%',
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  userSelect: 'none',
};

const CRYPTO_OPTS = [
  { key: 'btc', label: 'Bitcoin', symbol: 'BTC', icon: '/emoji/btc.png' },
  { key: 'eth', label: 'Ethereum', symbol: 'ETH', icon: null },
  { key: 'usdt_trx', label: 'USDT (TRC20)', symbol: 'USDT', icon: null },
  { key: 'ltc', label: 'Litecoin', symbol: 'LTC', icon: null },
];

const ACT_PWA_KEY = 'activationPwaToken';

const readActPwaToken = () => {
  try {
    return localStorage.getItem(ACT_PWA_KEY)
      || localStorage.getItem('pwaToken') // legacy
      || null;
  } catch { return null; }
};

const writeActPwaToken = (tok) => {
  if (!tok) return;
  try { localStorage.setItem(ACT_PWA_KEY, String(tok)); } catch { /* */ }
  // Ne plus polluer le token boutique avec le token activation
};

const haptic = () => {
  try {
    const hf = window.Telegram?.WebApp?.HapticFeedback;
    if (hf?.impactOccurred) hf.impactOccurred('light');
  } catch { /* */ }
};

const openExternal = (url) => {
  if (!url) return;
  try {
    const tg = window.Telegram?.WebApp;
    if (tg?.openTelegramLink && String(url).includes('t.me')) {
      tg.openTelegramLink(url);
      return;
    }
    if (tg?.openLink) {
      tg.openLink(url);
      return;
    }
  } catch { /* */ }
  try { window.open(url, '_blank'); } catch { /* */ }
};

/** FOMO inscriptions du jour — courbe humaine, cible minuit entre 25 et 40 */
function hashDaySeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function getParisDayParts(d = new Date()) {
  try {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Zurich',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
    const parts = Object.fromEntries(fmt.formatToParts(d).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
    const hour = parseInt(parts.hour, 10) || 0;
    const minute = parseInt(parts.minute, 10) || 0;
    const second = parseInt(parts.second, 10) || 0;
    const dateKey = `${parts.year}-${parts.month}-${parts.day}`;
    const dayProgress = (hour * 3600 + minute * 60 + second) / 86400;
    return { dateKey, dayProgress, hour };
  } catch {
    // WebView Telegram parfois sans timeZone IANA → fallback local
    const hour = d.getHours();
    const minute = d.getMinutes();
    const second = d.getSeconds();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return {
      dateKey: `${y}-${m}-${day}`,
      dayProgress: (hour * 3600 + minute * 60 + second) / 86400,
      hour,
    };
  }
}

function getTodaySignupsCount(now = new Date()) {
  const { dateKey, dayProgress, hour } = getParisDayParts(now);
  const seed = hashDaySeed(`gs-signup-${dateKey}`);
  const targetEnd = 25 + (seed % 16); // 25..40 à minuit
  // Courbe douce matin → accélération fin d'aprem / soir (FOMO)
  let curve = Math.pow(Math.min(1, Math.max(0, dayProgress)), 0.72);
  if (hour >= 18 && hour < 22) curve = Math.min(1, curve + 0.06); // bump soir
  if (hour >= 12 && hour < 14) curve = Math.min(1, curve + 0.03); // petit bump midi
  // Micro-variation « humaine » (stable ~2 min)
  const tick = Math.floor(now.getTime() / 120000);
  const wobbleSeed = hashDaySeed(`${dateKey}-${tick}`);
  const wobble = ((wobbleSeed % 5) - 2) * 0.35; // -0.7 .. +0.7
  let n = Math.round(targetEnd * curve + wobble);
  // Garde une présence ~autour de 30 en journée avancée
  if (dayProgress > 0.45 && dayProgress < 0.92) {
    const pull = 28 + (seed % 5); // 28..32
    n = Math.round(n * 0.55 + pull * 0.45);
  }
  n = Math.max(4, Math.min(targetEnd, n));
  if (dayProgress >= 0.995) n = targetEnd;
  return n;
}

const AccessModal = ({
  open,
  chatId,
  backendUrl,
  onBlocked,
  onReady,
  onEnterPreview,
  onActivationConfirmed,
  entryStep = null,
}) => {
  const [step, setStep] = useState('welcome');
  const [nameIntent, setNameIntent] = useState('signup'); // signup | preview
  const [name, setName] = useState('');
  const [country, setCountry] = useState(null);
  const [city, setCity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [cryptoInfo, setCryptoInfo] = useState(null);
  const [depositId, setDepositId] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [showSelfie, setShowSelfie] = useState(false);
  const [accessChatOpen, setAccessChatOpen] = useState(false);
  const [accessChatMinimized, setAccessChatMinimized] = useState(false);
  const [accessChatMessages, setAccessChatMessages] = useState([]);
  const [accessChatInput, setAccessChatInput] = useState('');
  const [accessChatUnread, setAccessChatUnread] = useState(0);
  const [accessChatSending, setAccessChatSending] = useState(false);
  const [accessChatUploading, setAccessChatUploading] = useState(false);
  const [accessChatUploadProgress, setAccessChatUploadProgress] = useState(0);
  const [accessChatUploadingMessageId, setAccessChatUploadingMessageId] = useState(null);
  const [accessChatError, setAccessChatError] = useState('');

  const resolveMediaUrl = useCallback((url) => {
    if (!url) return '';
    if (String(url).startsWith('blob:')) return url;
    // nginx SPA sur /uploads → fichiers via /api/uploads
    let pathname = String(url);
    let search = '';
    if (/^https?:\/\//i.test(pathname)) {
      try {
        const u = new URL(pathname);
        pathname = u.pathname || '/';
        search = u.search || '';
      } catch {
        return String(url);
      }
    } else if (!pathname.startsWith('/')) {
      pathname = `/${pathname}`;
    }
    pathname = pathname.replace(/^\/api\/uploads\//i, '/uploads/');
    if (pathname.startsWith('/uploads/')) pathname = `/api${pathname}`;
    const be = String(backendUrl || '').replace(/\/$/, '').replace(/\/api$/i, '');
    if (be && !/localhost|127\.0\.0\.1/i.test(be)) {
      try {
        const o = new URL(be.startsWith('http') ? be : `https://${be}`);
        return `${o.origin}${pathname}${search}`;
      } catch { /* */ }
    }
    return `https://global-strains.shop${pathname}${search}`;
  }, [backendUrl]);
  const [payMethod, setPayMethod] = useState(null);
  const [claimChannel, setClaimChannel] = useState(null);
  const accessChatPollRef = useRef(null);
  const sessionRestoredRef = useRef(false);
  const [eligibility, setEligibility] = useState(null);
  const [pwaToken, setPwaToken] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('t')
        || readActPwaToken()
        || null;
    } catch { return null; }
  });
  const [autoRedirectIn, setAutoRedirectIn] = useState(null);
  const [signupsToday, setSignupsToday] = useState(() => getTodaySignupsCount());
  const pollRef = useRef(null);
  const nameInputRef = useRef(null);

  const buildAuthHeaders = useCallback((extra = {}) => {
    const h = getApiAuthHeaders(extra);
    if (pwaToken) h['x-pwa-token'] = pwaToken;
    // Jamais de deviceId boutique sur Access (conflit d'identité)
    try { delete h['x-device-id']; } catch { /* */ }
    return h;
  }, [pwaToken]);

  useEffect(() => {
    if (!open) {
      sessionRestoredRef.current = false;
      return undefined;
    }
    // Persister le token d'activation (?t=) dès l'ouverture
    try {
      const t = new URLSearchParams(window.location.search).get('t');
      if (t) {
        writeActPwaToken(t);
        setPwaToken(t);
      }
    } catch { /* */ }
    setSignupsToday(getTodaySignupsCount());
    const id = setInterval(() => setSignupsToday(getTodaySignupsCount()), 45000);
    return () => clearInterval(id);
  }, [open]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPoll(), [stopPoll]);

  useEffect(() => {
    if (!open || !backendUrl) return;
    let cancelled = false;
    const run = async () => {
      try {
        const token = pwaToken
          || (() => { try { return new URLSearchParams(window.location.search).get('t'); } catch { return null; } })()
          || (() => { try { return readActPwaToken(); } catch { return null; } })();
        const hasInit = !!(window.Telegram?.WebApp?.initData);
        if (!chatId && !token && !hasInit) return;

        const q = new URLSearchParams();
        if (chatId) q.set('chatId', chatId);
        if (token) q.set('t', token);
        const res = await fetch(`${backendUrl}/activation/eligibility?${q.toString()}`, {
          headers: buildAuthHeaders(),
        });
        const json = await res.json();
        if (cancelled) return;
        if (!json.success && (res.status === 401 || res.status === 403)) {
          setError('Session expirée. Rouvre le lien depuis le bot d\'activation.');
          return;
        }
        setEligibility(json);
        if (json.claimChannel) setClaimChannel(json.claimChannel);
        if (json.pwaToken) {
          setPwaToken(json.pwaToken);
          try { writeActPwaToken(json.pwaToken); } catch { /* */ }
        }
        // Pré-remplir le nom depuis eligibility dans tous les cas (évite bouton figé)
        if (json.name) {
          const nm = String(json.name).trim();
          if (nm.length >= 2) {
            setName(nm);
            try {
              sessionStorage.setItem('gs_access_name', nm);
              localStorage.setItem('gs_access_name', nm);
            } catch { /* */ }
          }
        }
        // Accès déjà validé → UI claim (pas d'écran vide)
        if (json.reason === 'access_won' || json.activationCompleted === true) {
          try { sessionStorage.setItem('gs_access_won', '1'); } catch { /* */ }
          setStep('confirmed');
          return;
        }
        if (json.eligible === false) {
          if (typeof onBlocked === 'function') onBlocked(json.reason || 'already_client');
        } else if (json.eligible && typeof onReady === 'function') {
          onReady(json);
        }
      } catch { /* laisser tenter */ }
    };
    run();
    // initData Telegram parfois dispo un tick plus tard
    const t1 = setTimeout(run, 500);
    const t2 = setTimeout(run, 1600);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
  }, [open, chatId, backendUrl, pwaToken, buildAuthHeaders, onBlocked, onReady]);

  // Auto-redirect canal après confirmation
  useEffect(() => {
    if (step !== 'confirmed' || !claimChannel) return;
    setAutoRedirectIn(4);
    const tick = setInterval(() => {
      setAutoRedirectIn((n) => {
        if (n == null) return null;
        if (n <= 1) {
          clearInterval(tick);
          openExternal(claimChannel);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [step, claimChannel]);

  const go = (next) => {
    haptic();
    setError('');
    setStep(next);
  };

  // Reprise depuis « Obtenir un accès » (preview) → étape nom inscription
  // ⚠️ Ne jamais écraser un état terminal (confirmed / waiting / blocked)
  useEffect(() => {
    if (!open || !entryStep) return;
    // Si l'user est déjà confirmé (access_won côté eligibility ou session restore),
    // ne pas forcer l'étape nom — ça crée une race condition avec le fetch eligibility.
    setStep((currentStep) => {
      if (currentStep === 'confirmed' || currentStep === 'waiting' || currentStep === 'blocked') {
        return currentStep;
      }
      if (entryStep === 'name' || entryStep === 'fee' || entryStep === 'welcome') {
        setNameIntent('signup');
        // Pré-remplir le nom depuis le storage si disponible (fee ou name)
        if (entryStep === 'name' || entryStep === 'fee') {
          try {
            const saved = String(
              localStorage.getItem('gs_access_name') ||
              sessionStorage.getItem('gs_access_name') || ''
            ).trim();
            if (saved.length >= 2) setName((n) => (n && n.trim().length >= 2 ? n : saved));
          } catch { /* */ }
        }
        return entryStep;
      }
      return currentStep;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entryStep]);

  useEffect(() => {
    if (step !== 'confirmed') return;
    try { sessionStorage.setItem('gs_access_won', '1'); } catch { /* */ }
    if (typeof onActivationConfirmed === 'function') onActivationConfirmed();
  }, [step, onActivationConfirmed]);

  const saveNameAndContinue = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    haptic();
    const nm = String(name || '').trim();
    if (nm.length < 2) {
      setError('Entre un pseudo d\'au moins 2 caractères.');
      try { nameInputRef.current?.focus(); } catch { /* */ }
      return;
    }
    if (nm.length > 32) {
      setError('Pseudo trop long (max 32).');
      return;
    }
    setName(nm);
    try {
      sessionStorage.setItem('gs_access_name', nm);
      localStorage.setItem('gs_access_name', nm);
    } catch { /* */ }
    setError('');
    if (nameIntent === 'preview' && typeof onEnterPreview === 'function') {
      onEnterPreview(nm);
      return;
    }
    setStep('fee');
  };

  const startPolling = useCallback((id) => {
    stopPoll();
    const tick = async () => {
      try {
        const token = pwaToken || (() => { try { return readActPwaToken(); } catch { return null; } })();
        const q = token ? `?t=${encodeURIComponent(token)}` : '';
        const res = await fetch(`${backendUrl}/activation/deposit/${encodeURIComponent(id)}${q}`, {
          headers: buildAuthHeaders(),
        });
        const json = await res.json();
        if (!json.success || !json.deposit) return;
        const d = json.deposit;
        if (d.claimChannel) setClaimChannel(d.claimChannel);
        if (d.pwaToken) {
          setPwaToken(d.pwaToken);
          try { writeActPwaToken(d.pwaToken); } catch { /* */ }
          // Écrire aussi dans pwaToken boutique pour que le signet fonctionne dès la prochaine ouverture
          try { localStorage.setItem('pwaToken', d.pwaToken); } catch { /* */ }
        }
        if (d.status === 'confirmed') {
          setStep('confirmed');
          setShowSelfie(false);
          stopPoll();
        } else if (d.status === 'banned') {
          stopPoll();
          setShowSelfie(false);
          setAccessChatOpen(false);
          setCryptoInfo(null);
          setDepositId(null);
          setStep('blocked');
          setError('Vérification non effectuée — accès banni.');
        } else if (d.status === 'cancelled') {
          stopPoll();
          setShowSelfie(false);
          setCryptoInfo(null);
          setDepositId(null);
          setCancelConfirm(false);
          setStep('pay');
          setError('');
        } else if (d.needsSelfie || d.verifyRequested) {
          // Admin a demandé la vérif → modal bloquant
          setShowSelfie(true);
          setAccessChatOpen(false);
        } else {
          setShowSelfie(false);
        }
        if (typeof d.clientUnread === 'number' && d.clientUnread > 0) {
          setAccessChatUnread((prev) => Math.max(prev, d.clientUnread));
        }
      } catch (e) { /* poll soft-fail */ }
    };
    tick();
    pollRef.current = setInterval(tick, 4000);
  }, [backendUrl, stopPoll, pwaToken, buildAuthHeaders]);

  // Reprise session (QR / PCS) — dès que l'auth est prête, sans attendre eligibility
  useEffect(() => {
    if (!open || !backendUrl || sessionRestoredRef.current) return;
    // Bloqué seulement si déjà client confirmé
    if (eligibility && eligibility.eligible === false) return;
    let cancelled = false;
    (async () => {
      try {
        // Après annulation volontaire → rester sur le choix de paiement
        try {
          if (sessionStorage.getItem('gs_access_force_pay') === '1') {
            sessionStorage.removeItem('gs_access_force_pay');
            sessionRestoredRef.current = true;
            setStep('pay');
            return;
          }
        } catch { /* */ }

        const token = pwaToken
          || (() => { try { return new URLSearchParams(window.location.search).get('t'); } catch { return null; } })()
          || (() => { try { return readActPwaToken(); } catch { return null; } })();
        const hasInit = !!(window.Telegram?.WebApp?.initData);
        if (!chatId && !token && !hasInit) return;

        const sr = await fetch(`${backendUrl}/activation/session`, { headers: buildAuthHeaders() });
        const sess = await sr.json().catch(() => ({}));
        if (cancelled) return;
        if (!sess?.success || !sess.active) {
          sessionRestoredRef.current = true;
          // Pas de dossier vivant → nettoyer un éventuel état « waiting » périmé
          stopPoll();
          setDepositId(null);
          setCryptoInfo(null);
          setPayMethod(null);
          setShowSelfie(false);
          setAccessChatOpen(false);
          setAccessChatMessages([]);
          setCancelConfirm(false);
          setStep((s) => (s === 'waiting' || s === 'confirmed' ? 'pay' : s));
          return;
        }
        sessionRestoredRef.current = true;
        if (sess.name) setName(sess.name);
        if (sess.country) setCountry(sess.country);
        if (sess.city) setCity(sess.city);
        if (sess.depositId) setDepositId(sess.depositId);
        if (sess.pwaToken) {
          setPwaToken(sess.pwaToken);
          try { writeActPwaToken(sess.pwaToken); } catch { /* */ }
          // Écrire aussi dans pwaToken boutique pour que le signet résout le nouveau compte
          try { localStorage.setItem('pwaToken', sess.pwaToken); } catch { /* */ }
        }
        if (sess.claimChannel) setClaimChannel(sess.claimChannel);
        setPayMethod(sess.method || null);
        if (sess.crypto) {
          setCryptoInfo(sess.crypto);
          setStep('waiting');
          startPolling(sess.depositId);
        } else if (sess.method === 'pcs' || sess.method === 'paysafecard') {
          setCryptoInfo(null);
          setStep('waiting');
          setAccessChatOpen(false);
          if (sess.needsSelfie || sess.verifyRequested) setShowSelfie(true);
          else setShowSelfie(false);
          if (sess.depositId) startPolling(sess.depositId);
        }
      } catch { /* soft */ }
    })();
    return () => { cancelled = true; };
  }, [open, backendUrl, eligibility, pwaToken, chatId, buildAuthHeaders, startPolling, stopPoll]);

  const mapAccessMsgs = useCallback((msgs) => (msgs || []).map((m) => {
    const isImage = m.type === 'image' || m.mediaType === 'image';
    return {
      ...m,
      type: isImage ? 'image' : (m.type || 'text'),
      url: isImage ? resolveMediaUrl(m.url || m.mediaUrl) : (m.url || null),
      content: m.content || m.text || '',
    };
  }), [resolveMediaUrl]);

  const loadAccessChat = useCallback(async (id, { markRead = false } = {}) => {
    if (!id || !backendUrl) return;
    try {
      const q = markRead ? '?markRead=1' : '';
      const res = await fetch(`${backendUrl}/activation/deposit/${encodeURIComponent(id)}/chat${q}`, {
        headers: buildAuthHeaders(markRead ? { 'x-mark-read': '1' } : {}),
      });
      const json = await res.json();
      if (!json.success) return;
      setAccessChatMessages(mapAccessMsgs(json.messages || []));
      if (markRead) setAccessChatUnread(0);
      else if (typeof json.clientUnread === 'number') {
        setAccessChatUnread(json.clientUnread);
      }
    } catch { /* */ }
  }, [backendUrl, buildAuthHeaders, mapAccessMsgs]);

  useEffect(() => {
    if (!depositId || !(payMethod === 'pcs' || payMethod === 'paysafecard') || step !== 'waiting') {
      if (accessChatPollRef.current) {
        clearInterval(accessChatPollRef.current);
        accessChatPollRef.current = null;
      }
      return undefined;
    }
    const tick = () => loadAccessChat(depositId, { markRead: !!accessChatOpen });
    tick();
    accessChatPollRef.current = setInterval(tick, 4000);
    return () => {
      if (accessChatPollRef.current) clearInterval(accessChatPollRef.current);
    };
  }, [depositId, payMethod, step, loadAccessChat, accessChatOpen]);

  const sendAccessChatText = async () => {
    const text = String(accessChatInput || '').trim();
    if (!text || !depositId || accessChatSending) return;
    setAccessChatSending(true);
    setAccessChatError('');
    try {
      const token = pwaToken || (() => { try { return readActPwaToken(); } catch { return null; } })();
      // Même payload que POST /orders/:id/chat (content + role)
      const res = await fetch(`${backendUrl}/activation/deposit/${encodeURIComponent(depositId)}/chat`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          content: text,
          text,
          role: 'client',
          pwaToken: token || undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (json.success) {
        setAccessChatInput('');
        setAccessChatMessages(mapAccessMsgs(json.messages || []));
      } else {
        setAccessChatError(safeUserError(json.message || 'Message non envoyé'));
      }
    } catch {
      setAccessChatError(GENERIC_USER_ERROR);
    }
    setAccessChatSending(false);
  };

  const sendAccessChatPhoto = async (event) => {
    const rawFile = event?.target?.files?.[0] || (event instanceof File ? event : null);
    if (event?.target) {
      try { event.target.value = ''; } catch { /* */ }
    }
    if (!rawFile || !depositId || accessChatUploading) return;

    let file = rawFile;
    let localUrl = null;
    let tempId = null;
    let realMsgId = null;
    let uploadOk = false;

    const ensureStore = () => {
      try {
        if (typeof window !== 'undefined' && !(window.__gsChatBlobById instanceof Map)) {
          window.__gsChatBlobById = new Map();
        }
      } catch { /* */ }
    };
    const setBlob = (id, url) => {
      try {
        ensureStore();
        if (id && url) window.__gsChatBlobById.set(String(id), url);
      } catch { /* */ }
    };
    const delBlob = (id) => {
      try {
        if (id && window?.__gsChatBlobById instanceof Map) window.__gsChatBlobById.delete(String(id));
      } catch { /* */ }
    };

    try {
      try {
        file = await prepareChatImage(rawFile);
      } catch (prepErr) {
        setAccessChatError(safeUserError(prepErr?.message || 'Photo illisible — envoie un JPG/PNG'));
        return;
      }

      localUrl = URL.createObjectURL(file);
      tempId = `tmp_acc_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      ensureStore();
      setBlob(tempId, localUrl);

      const tempMsg = {
        id: tempId,
        role: 'client',
        type: 'image',
        url: localUrl,
        mediaUrl: localUrl,
        content: '',
        authorName: name || 'Moi',
        createdAt: new Date().toISOString(),
      };

      setAccessChatMessages((prev) => [...prev, tempMsg]);
      setAccessChatUploadingMessageId(tempId);
      setAccessChatUploadProgress(1);
      setAccessChatUploading(true);
      setAccessChatError('');

      const token = pwaToken || (() => { try { return readActPwaToken(); } catch { return null; } })();
      const form = new FormData();
      form.append('file', file, file.name || 'photo.jpg');
      form.append('role', 'client');
      form.append('mediaType', 'image');
      if (token) form.append('pwaToken', token);

      const headers = buildAuthHeaders();
      try { delete headers['Content-Type']; delete headers['content-type']; } catch { /* */ }

      const result = await uploadWithProgress(
        `${backendUrl}/activation/deposit/${encodeURIComponent(depositId)}/chat/media`,
        form,
        {
          method: 'POST',
          headers,
          timeout: 180000,
          onProgress: (p) => {
            const safe = Math.max(1, Math.min(99, Number(p) || 0));
            setAccessChatUploadProgress(safe);
          },
        }
      );

      let json = { success: false };
      try {
        const raw = await result.text();
        try { json = raw ? JSON.parse(raw) : {}; } catch { json = { success: result.ok, _rawText: raw }; }
      } catch {
        json = { success: result.ok };
      }

      if (json && json.success) {
        uploadOk = true;
        setAccessChatUploadProgress(100);
        let nextMessages = [];
        if (json.messages && Array.isArray(json.messages) && json.messages.length > 0) {
          const mapped = mapAccessMsgs(json.messages);
          const mine = [...mapped].reverse().find((m) => m && m.role === 'client' && (m.type === 'image' || m.type === 'video'));
          if (mine?.id) {
            realMsgId = mine.id;
            setBlob(mine.id, localUrl);
          }
          nextMessages = mapped;
          setAccessChatMessages(nextMessages);
        } else {
          setAccessChatMessages((prev) => {
            const withoutTemp = prev.filter((m) => m.id !== tempId);
            if (json.message && typeof json.message === 'object') {
              const arr = [json.message];
              const oneMapped = mapAccessMsgs(arr);
              const single = oneMapped[0];
              if (single?.id) {
                realMsgId = single.id;
                setBlob(single.id, localUrl);
              }
              return [...withoutTemp, single].filter(Boolean);
            }
            return withoutTemp;
          });
        }
      } else {
        setAccessChatMessages((prev) => prev.filter((m) => m.id !== tempId));
        setAccessChatError(safeUserError((json && json.message) || 'Photo non envoyée'));
      }
    } catch (err) {
      if (tempId) {
        setAccessChatMessages((prev) => prev.filter((m) => m.id !== tempId));
      }
      setAccessChatError(GENERIC_USER_ERROR);
    } finally {
      if (tempId) {
        delBlob(tempId);
      }
      if (!uploadOk) {
        if (realMsgId) delBlob(realMsgId);
        if (localUrl) { try { URL.revokeObjectURL(localUrl); } catch { /* */ } }
      }
      setAccessChatUploading(false);
      setAccessChatUploadProgress(0);
      setAccessChatUploadingMessageId(null);
    }
  };

  const submitSelfie = async (file) => {
    const form = new FormData();
    form.append('selfie', file);
    form.append('name', name || '');
    if (depositId) form.append('depositId', depositId);
    const token = pwaToken || (() => { try { return readActPwaToken(); } catch { return null; } })();
    if (token) form.append('pwaToken', token);

    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => { try { ctrl?.abort(); } catch { /* */ } }, 28000);
    let res;
    try {
      res = await fetch(`${backendUrl}/activation/verify-selfie`, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: form,
        signal: ctrl?.signal,
      });
    } finally {
      clearTimeout(timer);
    }
    const json = await res.json().catch(() => ({}));
    if (!json.success) throw new Error(json.message || 'fail');
    await new Promise((r) => setTimeout(r, 1800));
    setShowSelfie(false);
    setAccessChatOpen(false);
    setAccessChatUnread(0);
    if (json.depositId) {
      setDepositId(json.depositId);
      await loadAccessChat(json.depositId);
    }
  };

  const cancelDeposit = async () => {
    haptic();
    if (!depositId) {
      stopPoll();
      setCryptoInfo(null);
      setCancelConfirm(false);
      setAccessChatMessages([]);
      setPayMethod(null);
      setStep('pay');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const token = pwaToken || (() => { try { return readActPwaToken(); } catch { return null; } })();
      const res = await fetch(`${backendUrl}/activation/deposit/${encodeURIComponent(depositId)}/cancel`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          pwaToken: token || undefined,
          chatId: chatId ? String(chatId) : undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      // Même si l'API échoue, libérer l'UI — un nouveau /start supersede le dossier
      stopPoll();
      setCryptoInfo(null);
      setDepositId(null);
      setCancelConfirm(false);
      setShowSelfie(false);
      setAccessChatOpen(false);
      setAccessChatMinimized(false);
      setAccessChatMessages([]);
      setAccessChatUnread(0);
      setPayMethod(null);
      sessionRestoredRef.current = true;
      try { sessionStorage.setItem('gs_access_force_pay', '1'); } catch { /* */ }
      setStep('pay');
      if (!json.success) {
        setError(safeUserError(json.message || 'Demande clôturée — choisis un autre paiement.'));
      }
    } catch {
      stopPoll();
      setDepositId(null);
      setCryptoInfo(null);
      setPayMethod(null);
      setShowSelfie(false);
      setAccessChatOpen(false);
      setStep('pay');
      setError(GENERIC_USER_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const startDeposit = async (method) => {
    haptic();
    const token = pwaToken
      || (() => { try { return new URLSearchParams(window.location.search).get('t'); } catch { return null; } })()
      || (() => { try { return readActPwaToken(); } catch { return null; } })();
    const hasInit = !!(window.Telegram?.WebApp?.initData);
    if (!chatId && !token && !hasInit) {
      setError('Identifiant Telegram introuvable. Rouvre depuis le bot.');
      return;
    }
    const nm = String(name || '').trim();
    if (nm.length < 2) {
      setError('Pseudo manquant — retourne à l\'étape nom.');
      setStep('name');
      return;
    }
    if (country !== 'suisse' || city !== 'Genève') {
      setStep('blocked');
      return;
    }

    setBusy(true);
    setError('');
    try {
      if (token) {
        try { writeActPwaToken(token); } catch { /* */ }
        setPwaToken(token);
      }
      const res = await fetch(`${backendUrl}/activation/start`, {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          chatId: chatId ? String(chatId) : undefined,
          name: nm,
          country,
          city,
          method,
          pwaToken: token || undefined,
        }),
      });
      const json = await res.json();
      if (!json.success) {
        if (json.code === 'CITY_BLOCKED') {
          setStep('blocked');
          return;
        }
        setError(safeUserError(json.message));
        return;
      }

      if (json.claimChannel) setClaimChannel(json.claimChannel);
      if (json.pwaToken) {
        setPwaToken(json.pwaToken);
        try { writeActPwaToken(json.pwaToken); } catch { /* */ }
      }

      if (json.depositId) setDepositId(json.depositId);
      setCancelConfirm(false);
      setPayMethod(method);
      setAccessChatMessages([]);
      try { sessionStorage.removeItem('gs_access_force_pay'); } catch { /* */ }

      if (method === 'pcs' || method === 'paysafecard') {
        setCryptoInfo(null);
        setStep('waiting');
        setShowSelfie(false);
        setAccessChatOpen(false);
        startPolling(json.depositId);
        return;
      }

      setCryptoInfo(json.crypto || null);
      setStep('waiting');
      startPolling(json.depositId);
    } catch {
      setError(GENERIC_USER_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const copyAddress = async () => {
    haptic();
    const addr = cryptoInfo?.address;
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(addr);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = addr;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* */ }
    }
  };

  if (!open) return null;
  if (typeof document === 'undefined' || !document.body) return null;

  // Accès déjà gagné → même flux claim (géré dans le modal principal via step confirmed)
  const accessWon = eligibility
    && (eligibility.reason === 'access_won' || eligibility.activationCompleted === true);

  if (eligibility && eligibility.eligible === false && !accessWon) {
    const needBot = eligibility.reason === 'need_bot_start';
    return createPortal(
      <div style={OVERLAY_S}>
        <div style={{ ...OVERLAY_S, backgroundImage: 'none', background: 'rgba(5,10,30,0.78)', position: 'absolute' }} />
        <motion.div initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={MODAL_S}>
          <div style={{ textAlign: 'center' }}>
            <img src="/emoji/logoapp1.png" alt="" style={{ width: 64, height: 64, objectFit: 'contain', margin: '0 auto 16px' }} />
            <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 900 }}>
              {needBot ? 'Passe par le bot d\'abord' : 'Accès indisponible'}
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: 'rgba(191,219,254,0.85)', lineHeight: 1.6 }}>
              {needBot
                ? (eligibility.message || 'Ouvre le bot d\'activation Telegram, fais /start et le captcha, puis clique sur « Obtenir mon accès ».')
                : (eligibility.message || 'Ce système est réservé aux nouveaux comptes. Tu as déjà un compte Global Strains.')}
            </p>
          </div>
        </motion.div>
      </div>,
      document.body
    );
  }

  const titles = {
    welcome: 'Access',
    name: 'Ton identifiant',
    fee: '1ʳᵉ commande offerte',
    country: 'Zone de livraison',
    city: 'Ta ville',
    blocked: 'Zone non couverte',
    pay: 'Paiement',
    crypto: 'Crypto',
    waiting: 'En attente',
    confirmed: 'Accès activé',
  };

  const mainPortal = createPortal(
    <div style={OVERLAY_S}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, rgba(8,16,36,0.72) 0%, rgba(12,28,64,0.55) 45%, rgba(8,16,36,0.82) 100%)',
        pointerEvents: 'none',
      }} />
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ scale: 0.94, opacity: 0, y: 14 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.97, opacity: 0, y: -8 }}
          transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
          style={MODAL_S}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            position: 'absolute', top: -40, right: -30, width: 140, height: 140, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(96,165,250,0.28), transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{ textAlign: 'center', marginBottom: 16, position: 'relative' }}>
            <img
              src={GS_LOGO}
              alt="Global Strains"
              style={{
                width: 96,
                height: 96,
                objectFit: 'contain', margin: '0 auto 10px',
                filter: 'drop-shadow(0 10px 28px rgba(251, 191, 36, 0.4))',
              }}
            />
            <h2 style={{
              margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #e0f7ff 0%, #7dd3fc 45%, #c4b5fd 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              {titles[step] || 'Access'}
            </h2>
            <div style={{
              marginTop: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 999,
              background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(191,219,254,0.22)',
            }}>
              <span style={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
                <span style={{
                  position: 'absolute', inset: 0, borderRadius: '50%', background: '#ef4444',
                  animation: 'gsAccessPulse 1.4s ease-in-out infinite',
                }} />
                <span style={{
                  position: 'absolute', inset: 0, borderRadius: '50%', background: '#ef4444', opacity: 0.55,
                  animation: 'gsAccessPing 1.4s cubic-bezier(0,0,0.2,1) infinite',
                }} />
              </span>
              <span style={{
                fontSize: 12, fontWeight: 700, color: 'rgba(254,243,199,0.95)',
                textShadow: '0 1px 8px rgba(0,0,0,0.45)',
              }}>
                {signupsToday} personne{signupsToday > 1 ? 's' : ''} se sont inscrit{signupsToday > 1 ? 'es' : 'e'} aujourd&apos;hui
              </span>
            </div>
            <style>{`
              @keyframes gsAccessPulse { 0%,100%{opacity:1} 50%{opacity:0.55} }
              @keyframes gsAccessPing {
                0%{transform:scale(1);opacity:0.7}
                75%,100%{transform:scale(2.2);opacity:0}
              }
            `}</style>
          </div>

          {!chatId && step !== 'waiting' && step !== 'confirmed' && step !== 'blocked' && (
            <div style={{
              ...GLASS, borderRadius: 12, padding: '10px 12px', marginBottom: 12,
              border: '1px solid rgba(251,191,36,0.45)', color: '#fde68a', fontSize: 12, fontWeight: 600,
            }}>
              Connexion Telegram en cours… Si ça bloque, ferme et rouvre depuis le bot.
            </div>
          )}

          {error && (
            <div style={{
              ...GLASS, borderRadius: 12, padding: '10px 12px', marginBottom: 12,
              border: '1px solid rgba(248,113,113,0.45)', color: '#fecaca', fontSize: 12, fontWeight: 600,
            }}>
              {error}
            </div>
          )}

          {step === 'welcome' && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 10px', fontSize: 14, lineHeight: 1.65, color: 'rgba(186,230,253,0.92)' }}>
                Bienvenue sur <strong style={{ color: '#fff' }}>Global Strains</strong>.
              </p>
              <p style={{ margin: '0 0 22px', fontSize: 13, lineHeight: 1.6, color: 'rgba(186,230,253,0.72)' }}>
                Inscris-toi pour obtenir ton accès, ou teste d&apos;abord l&apos;app en aperçu.
              </p>
              <button
                type="button"
                style={{ ...BTN_BLUE, marginBottom: 10 }}
                onClick={() => { setNameIntent('signup'); go('name'); }}
              >
                S&apos;inscrire
              </button>
              <button
                type="button"
                style={BTN_GLASS}
                onClick={() => { setNameIntent('preview'); go('name'); }}
              >
                Tester
              </button>
            </div>
          )}

          {step === 'name' && (
            <form onSubmit={saveNameAndContinue} style={{ margin: 0 }}>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'rgba(191,219,254,0.78)', textAlign: 'center' }}>
                {nameIntent === 'preview'
                  ? 'Choisis un pseudo pour le classement et l\'aperçu'
                  : 'Choisis le pseudo qui apparaîtra sur tes commandes'}
              </p>
              <input
                ref={nameInputRef}
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 32))}
                placeholder="Ton pseudo / prénom"
                maxLength={32}
                autoComplete="nickname"
                enterKeyHint="done"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '14px 14px', borderRadius: 12, marginBottom: 14,
                  background: 'rgba(255,255,255,0.10)',
                  border: '1.5px solid rgba(191,219,254,0.40)',
                  color: '#fff', fontSize: 16, fontWeight: 600, outline: 'none',
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  type="submit"
                  style={{ ...BTN_BLUE, opacity: name.trim().length < 2 ? 0.55 : 1 }}
                >
                  {nameIntent === 'preview' ? 'Voir le shop' : 'Continuer'}
                </button>
                <button type="button" style={{ ...BTN_GLASS, opacity: 0.7 }} onClick={() => go('welcome')}>
                  ← Retour
                </button>
              </div>
            </form>
          )}

          {step === 'fee' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                ...GLASS, borderRadius: 18, padding: '22px 18px', marginBottom: 16,
                border: '1.5px solid rgba(253,224,71,0.38)',
                background: 'linear-gradient(145deg, rgba(251,191,36,0.14) 0%, rgba(0,100,255,0.18) 55%, rgba(112,0,255,0.14) 100%)',
              }}>
                <p style={{
                  margin: '0 0 8px', fontSize: 10, fontWeight: 800, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: 'rgba(253,224,71,0.9)',
                }}>
                  Offre d&apos;inscription
                </p>
                <h3 style={{
                  margin: '0 0 10px', fontSize: 22, fontWeight: 900, lineHeight: 1.2, color: '#fff',
                }}>
                  Ta{' '}
                  <span style={{
                    background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 50%, #fb923c 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}>
                    1ʳᵉ commande offerte
                  </span>
                </h3>
                <p style={{
                  margin: '0 0 14px', fontSize: 13, lineHeight: 1.55, color: 'rgba(191,219,254,0.85)',
                }}>
                  Active ton accès et reçois <strong style={{ color: '#fde68a' }}>60 Global Coins</strong> —
                  de quoi passer ta première commande + 20GC .
                </p>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px', borderRadius: 999,
                  background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(253,224,71,0.35)',
                }}>
                  <span style={{
                    fontSize: 18, fontWeight: 900,
                    background: 'linear-gradient(135deg, #fef3c7, #fbbf24, #fb923c)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  }}>
                    +60 GC
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(254,243,199,0.75)' }}>
                    crédités à l&apos;activation
                  </span>
                </div>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'rgba(191,219,254,0.55)' }}>
                Pseudo : <strong style={{ color: '#fff' }}>{name}</strong>
              </p>
              <button type="button" style={BTN_BLUE} onClick={() => go('country')}>
                Continuer
              </button>
              <button type="button" style={{ ...BTN_GLASS, opacity: 0.65, marginTop: 10 }} onClick={() => go('name')}>
                ← Retour
              </button>
            </div>
          )}

          {step === 'country' && (
            <div>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: 'rgba(191,219,254,0.78)', textAlign: 'center' }}>
                Sélectionne ton pays de livraison
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button type="button" style={BTN_BLUE} onClick={() => { setCountry('suisse'); go('city'); }}>
                  🇨🇭 Suisse
                </button>
                <button type="button" style={BTN_GLASS} onClick={() => { setCountry('france'); go('city'); }}>
                  🇫🇷 France
                </button>
                <button type="button" style={{ ...BTN_GLASS, opacity: 0.65 }} onClick={() => go('fee')}>
                  ← Retour
                </button>
              </div>
            </div>
          )}

          {step === 'city' && (
            <div>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: 'rgba(191,219,254,0.78)', textAlign: 'center' }}>
                <MapPin size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />
                {country === 'suisse' ? 'Suisse' : 'France'} — choisis ta zone de livraison
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {country === 'suisse' && (
                  <button
                    type="button"
                    style={{
                      ...BTN_BLUE,
                      background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 55%, #d32f2f 100%)',
                      border: '1.5px solid rgba(255,255,255,0.35)',
                    }}
                    onClick={() => { setCity('Genève'); go('pay'); }}
                  >
                    Genève
                  </button>
                )}
                {country === 'france' && (
                  <button
                    type="button"
                    style={BTN_GLASS}
                    onClick={() => { setCity('Nantes'); go('blocked'); }}
                  >
                    Nantes
                  </button>
                )}
                <button type="button" style={{ ...BTN_GLASS, opacity: 0.7 }} onClick={() => go('country')}>
                  ← Retour
                </button>
              </div>
            </div>
          )}

          {step === 'blocked' && (
            <div style={{ textAlign: 'center' }}>
              <p style={{ margin: '0 0 18px', fontSize: 14, lineHeight: 1.65, color: 'rgba(191,219,254,0.88)' }}>
                L&apos;ouverture de compte est réservée à <strong>Genève</strong> pour le moment.
                Aucun paiement n&apos;est demandé hors de cette zone.
              </p>
              <button type="button" style={BTN_GLASS} onClick={() => { setCountry(null); setCity(null); go('country'); }}>
                Changer de zone
              </button>
            </div>
          )}

          {step === 'pay' && (
            <div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(191,219,254,0.78)', textAlign: 'center' }}>
                Choisis ton moyen de paiement · 50 CHF
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button type="button" style={BTN_BLUE} disabled={busy} onClick={() => go('crypto')}>
                  <img src="/emoji/btc.png" alt="" style={{ width: 34, height: 34, objectFit: 'contain', marginRight: 4 }} onError={(e) => { e.target.style.display = 'none'; }} />
                  Crypto
                </button>
                <button type="button" style={BTN_GLASS} disabled={busy} onClick={() => startDeposit('pcs')}>
                  <img src="/emoji/pcs.png" alt="" style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 4 }} onError={(e) => { e.target.style.display = 'none'; }} />
                  PCS
                </button>
                <button type="button" style={BTN_GLASS} disabled={busy} onClick={() => startDeposit('paysafecard')}>
                  <img src="/emoji/psf.png" alt="" style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 4 }} onError={(e) => { e.target.style.display = 'none'; }} />
                  Paysafecard
                </button>
                <button type="button" style={{ ...BTN_GLASS, opacity: 0.65 }} onClick={() => go('city')}>
                  ← Retour
                </button>
              </div>
            </div>
          )}

          {step === 'crypto' && (
            <div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(191,219,254,0.78)', textAlign: 'center' }}>
                Montant exact ≈ 50 CHF au taux du moment
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {CRYPTO_OPTS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    style={BTN_GLASS}
                    disabled={busy}
                    onClick={() => startDeposit(c.key)}
                  >
                    {busy ? <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                    {c.icon ? (
                      <img src={c.icon} alt="" style={{ width: 30, height: 30, objectFit: 'contain', marginRight: 6 }} />
                    ) : (
                      <span style={{ fontWeight: 900, fontSize: 16, minWidth: 56, textAlign: 'center' }}>{c.symbol}</span>
                    )}
                    {c.label}
                  </button>
                ))}
                <button type="button" style={{ ...BTN_GLASS, opacity: 0.65 }} onClick={() => go('pay')}>
                  ← Retour
                </button>
              </div>
            </div>
          )}

          {step === 'waiting' && (
            <div style={{ textAlign: 'center' }}>
              {cryptoInfo ? (
                <>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px', borderRadius: 999, marginBottom: 14,
                    background: 'rgba(59,130,246,0.18)',
                    border: '1px solid rgba(147,197,253,0.35)',
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#93c5fd', letterSpacing: '0.04em' }}>
                      {cryptoInfo.symbol}{cryptoInfo.network ? ` · ${cryptoInfo.network}` : ''}
                    </span>
                  </div>
                  <p style={{ margin: '0 0 16px', fontSize: 14, color: 'rgba(226,232,240,0.92)', lineHeight: 1.45 }}>
                    Envoie exactement{' '}
                    <strong style={{ color: '#fff', fontSize: 16 }}>{cryptoInfo.amount} {cryptoInfo.symbol}</strong>
                    <br />
                    <span style={{ fontSize: 12, color: 'rgba(191,219,254,0.55)' }}>≈ {cryptoInfo.chf} CHF</span>
                  </p>

                  {/* QR Trust-style · liquid glass */}
                  {cryptoInfo.qrImageUrl && (
                    <div style={{
                      margin: '0 auto 16px',
                      width: 'min(100%, 268px)',
                      padding: 14,
                      borderRadius: 28,
                      background: 'linear-gradient(145deg, rgba(96,165,250,0.22) 0%, rgba(30,64,175,0.28) 45%, rgba(8,20,60,0.55) 100%)',
                      border: '1.5px solid rgba(186,230,253,0.42)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.22)',
                      backdropFilter: 'blur(28px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(28px) saturate(180%)',
                      position: 'relative',
                    }}>
                      {/* coins décoratifs type Trust */}
                      <div style={{
                        position: 'absolute', top: 10, left: 10, width: 18, height: 18,
                        borderTop: '2.5px solid rgba(147,197,253,0.85)',
                        borderLeft: '2.5px solid rgba(147,197,253,0.85)',
                        borderRadius: '10px 0 0 0',
                      }} />
                      <div style={{
                        position: 'absolute', top: 10, right: 10, width: 18, height: 18,
                        borderTop: '2.5px solid rgba(147,197,253,0.85)',
                        borderRight: '2.5px solid rgba(147,197,253,0.85)',
                        borderRadius: '0 10px 0 0',
                      }} />
                      <div style={{
                        position: 'absolute', bottom: 10, left: 10, width: 18, height: 18,
                        borderBottom: '2.5px solid rgba(147,197,253,0.85)',
                        borderLeft: '2.5px solid rgba(147,197,253,0.85)',
                        borderRadius: '0 0 0 10px',
                      }} />
                      <div style={{
                        position: 'absolute', bottom: 10, right: 10, width: 18, height: 18,
                        borderBottom: '2.5px solid rgba(147,197,253,0.85)',
                        borderRight: '2.5px solid rgba(147,197,253,0.85)',
                        borderRadius: '0 0 10px 0',
                      }} />
                      <div style={{
                        borderRadius: 20,
                        padding: 14,
                        background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
                        boxShadow: '0 8px 24px rgba(15,23,42,0.28), inset 0 0 0 1px rgba(15,23,42,0.06)',
                      }}>
                        <img
                          src={cryptoInfo.qrImageUrl}
                          alt="QR paiement"
                          width={220}
                          height={220}
                          style={{
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            aspectRatio: '1 / 1',
                            borderRadius: 12,
                            background: '#fff',
                          }}
                        />
                      </div>
                      <p style={{
                        margin: '12px 0 0', fontSize: 11, fontWeight: 700,
                        letterSpacing: '0.12em', textTransform: 'uppercase',
                        color: 'rgba(191,219,254,0.7)',
                      }}>
                        Scan · wallet crypto
                      </p>
                    </div>
                  )}

                  <div style={{
                    ...GLASS, borderRadius: 16, padding: '12px 14px', marginBottom: 10,
                    wordBreak: 'break-all', fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    color: 'rgba(226,232,240,0.95)', textAlign: 'left',
                    border: '1.5px solid rgba(147,197,253,0.28)',
                  }}>
                    <p style={{ margin: '0 0 6px', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(147,197,253,0.7)' }}>
                      Adresse
                    </p>
                    {cryptoInfo.address}
                  </div>
                  <button type="button" style={{ ...BTN_GLASS, marginBottom: 14 }} onClick={copyAddress}>
                    {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                    {copied ? 'Adresse copiée' : 'Copier l\'adresse'}
                  </button>
                </>
              ) : (
                <p style={{ margin: '0 0 16px', fontSize: 13, lineHeight: 1.65, color: 'rgba(191,219,254,0.88)' }}>
                  Envoie un selfie {(payMethod || '').toUpperCase() || 'PCS'} dans le chat sécurisé
                  (en bas à droite). Un administrateur se chargera de vous.
                </p>
              )}
              <div style={{
                ...GLASS, borderRadius: 14, padding: '14px 12px', marginBottom: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}>
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(191,219,254,0.95)' }}>
                  En attente de validation admin…
                </span>
              </div>
              {(payMethod === 'pcs' || payMethod === 'paysafecard') && !showSelfie && (
                <button
                  type="button"
                  onClick={() => {
                    haptic();
                    if (!depositId) {
                      setAccessChatError('Session dépôt manquante — rouvre le lien du bot d\'accès.');
                      return;
                    }
                    setAccessChatMinimized(false);
                    setAccessChatOpen(true);
                    setAccessChatUnread(0);
                    setAccessChatError('');
                    loadAccessChat(depositId, { markRead: true });
                  }}
                  style={{ ...BTN_BLUE, marginBottom: 10 }}
                >
                  Ouvrir le chat Access
                  {accessChatUnread > 0 ? ` (${accessChatUnread})` : ''}
                </button>
              )}

              {!cancelConfirm ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => { haptic(); setCancelConfirm(true); }}
                  style={{
                    ...BTN_GLASS, marginTop: 4, opacity: 0.9,
                    border: '1px solid rgba(248,113,113,0.35)',
                    color: '#fca5a5',
                  }}
                >
                  Annuler
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                  <p style={{ margin: 0, fontSize: 12, color: 'rgba(252,165,165,0.9)', fontWeight: 600 }}>
                    êtes vous sûre de vouloir annuler ? 
                  </p>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={cancelDeposit}
                    style={{
                      ...BTN_BLUE,
                      background: 'linear-gradient(135deg, rgba(239,68,68,0.75), rgba(185,28,28,0.85))',
                      border: '1.5px solid rgba(252,165,165,0.45)',
                    }}
                  >
                    {busy ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                    Oui
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setCancelConfirm(false)}
                    style={{ ...BTN_GLASS, opacity: 0.75 }}
                  >
                    Non, continuer
                  </button>
                </div>
              )}
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {step === 'confirmed' && (
            <div style={{ textAlign: 'center', position: 'relative' }}>
              <div
                aria-hidden
                style={{
                  position: 'absolute', top: -30, left: '50%', marginLeft: -70, width: 140, height: 140,
                  borderRadius: '50%', pointerEvents: 'none',
                  background: 'radial-gradient(circle, rgba(74,222,128,0.28), transparent 70%)',
                }}
              />
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 220, damping: 14 }}
                style={{
                  width: 84, height: 84, borderRadius: '50%', margin: '0 auto 18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(145deg, rgba(74,222,128,0.32) 0%, rgba(34,197,94,0.22) 50%, rgba(16,185,129,0.28) 100%)',
                  border: '1.5px solid rgba(134,239,172,0.55)',
                  boxShadow: '0 0 48px rgba(34,197,94,0.38), inset 0 1px 0 rgba(255,255,255,0.28)',
                  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                  position: 'relative', zIndex: 1,
                }}
              >
                <CheckCircle size={40} color="#86efac" />
              </motion.div>
              <p style={{
                margin: '0 0 6px', fontSize: 10, fontWeight: 800, letterSpacing: '0.2em',
                textTransform: 'uppercase', color: 'rgba(134,239,172,0.9)', position: 'relative', zIndex: 1,
              }}>
                Paiement validé  
              </p>
              <h3 style={{
                margin: '0 0 10px', fontSize: 22, fontWeight: 900, lineHeight: 1.25,
                color: '#fff', position: 'relative', zIndex: 1,
              }}>
                {name ? `${String(name).split(/\s+/)[0]}, tu as` : 'Tu as'}{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #fde68a 0%, #fbbf24 50%, #4ade80 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>
                  Bienvenue à toi
                </span>
                
              </h3>
              <div style={{
                ...GLASS, borderRadius: 16, padding: '14px 16px', marginBottom: 18,
                border: '1.5px solid rgba(134,239,172,0.35)',
                background: 'linear-gradient(145deg, rgba(34,197,94,0.14) 0%, rgba(0,100,255,0.12) 100%)',
                position: 'relative', zIndex: 1,
              }}>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'rgba(191,219,254,0.9)' }}>
                  Compte activé · <strong style={{ color: '#fde68a' }}>+60 GC</strong> crédités.
                  <br />
                  Rejoin le canal et accède au shop
                </p>
              </div>
              {claimChannel ? (
                <>
                  <button
                    type="button"
                    style={{
                      ...BTN_BLUE,
                      background: 'linear-gradient(135deg, rgba(74,222,128,0.55) 0%, rgba(34,197,94,0.62) 45%, rgba(16,185,129,0.55) 100%)',
                      border: '1.5px solid rgba(134,239,172,0.65)',
                      boxShadow: '0 10px 32px rgba(34,197,94,0.45), inset 0 1px 0 rgba(255,255,255,0.35)',
                      fontSize: 15, padding: '16px 18px',
                    }}
                    onClick={() => { haptic(); openExternal(claimChannel); }}
                  >
                    Recupérer mon accès
                  </button>
                  {autoRedirectIn != null && autoRedirectIn > 0 && (
                    <p style={{ margin: '14px 0 0', fontSize: 12, color: 'rgba(191,219,254,0.55)' }}>
                      Ouverture du canal dans {autoRedirectIn}s…
                    </p>
                  )}
                </>
              ) : (
                <p style={{ fontSize: 12, color: 'rgba(248,113,113,0.9)' }}>
                  Lien canal indisponible — contacte le support.
                </p>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Bulle chat Access — même style / icône que la bulle commande de l'app */}
      {(payMethod === 'pcs' || payMethod === 'paysafecard') && step === 'waiting' && !showSelfie && !accessChatOpen && (
        <motion.button
          type="button"
          initial={{ opacity: 0, scale: 0.5, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          whileTap={{ scale: 0.93 }}
          onClick={() => {
            haptic();
            if (!depositId) {
              setAccessChatError('Session dépôt manquante — rouvre le lien du bot d\'accès.');
              return;
            }
            setAccessChatMinimized(false);
            setAccessChatOpen(true);
            setAccessChatUnread(0);
            setAccessChatError('');
            loadAccessChat(depositId, { markRead: true });
          }}
          aria-label="Ouvrir le chat Access"
          style={{
            position: 'fixed',
            bottom: 22,
            right: 16,
            zIndex: 'calc(var(--gs-z-lock, 4000) + 10)',
            width: 58,
            height: 58,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, rgba(74,144,226,0.85) 0%, rgba(30,80,180,0.9) 60%, rgba(10,20,80,0.92) 100%)',
            backdropFilter: 'blur(28px) saturate(220%) brightness(1.1)',
            WebkitBackdropFilter: 'blur(28px) saturate(220%) brightness(1.1)',
            border: '1.5px solid rgba(107,163,232,0.55)',
            boxShadow: '0 8px 28px rgba(74,144,226,0.45), 0 2px 10px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.25)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <MessageCircle className="w-6 h-6" style={{ color: '#fff', width: 24, height: 24 }} />
          {accessChatUnread > 0 && (
            <span style={{
              position: 'absolute', top: -2, right: -2, minWidth: 18, height: 18, padding: '0 5px',
              borderRadius: 999, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #0a1628',
            }}>
              {accessChatUnread > 9 ? '9+' : accessChatUnread}
            </span>
          )}
        </motion.button>
      )}
    </div>,
    document.body
  );

  // Selfie bloquant (portal séparé — au-dessus de tout)
  return (
    <>
      {mainPortal}
      
      <FloatingChatPanel
        title="Chat sécurisé"
        subtitle={`Access · ${(payMethod || '').toUpperCase()}`}
        isOpen={!!(accessChatOpen && depositId)}
        isMinimized={accessChatMinimized}
        onClose={() => { setAccessChatOpen(false); setAccessChatMinimized(false); }}
        onMinimize={() => setAccessChatMinimized((v) => !v)}
        messages={accessChatMessages}
        inputValue={accessChatInput}
        onInputChange={setAccessChatInput}
        onSend={sendAccessChatText}
        onPhotoUpload={sendAccessChatPhoto}
        photoInputId="access-client-chat-photo"
        photoAccept="image/*"
        unreadCount={accessChatUnread}
        role="client"
        sending={accessChatSending}
        uploading={accessChatUploading}
        uploadProgress={accessChatUploadProgress}
        uploadingMessageId={accessChatUploadingMessageId}
        placeholder="Message…"
        backendUrl={backendUrl || ''}
      />
      {accessChatError ? (
        <div style={{ position: 'fixed', bottom: 90, left: 16, right: 16, zIndex: 'calc(var(--gs-z-lock, 4000) + 20)', textAlign: 'center' }}>
          <span style={{ display: 'inline-block', padding: '8px 12px', borderRadius: 10, background: 'rgba(127,29,29,0.9)', color: '#fecaca', fontSize: 12, fontWeight: 700 }}>
            {accessChatError}
          </span>
        </div>
      ) : null}

      <AccessSelfieModal
        open={open && showSelfie}
        onConfirmFile={submitSelfie}
        onBusyChange={setBusy}
      />
    </>
  );
};

export default AccessModal;